import { IconChevronRight, IconClock } from '@tabler/icons-solidjs'
import type { ProductionWorkTask } from '@veem/schema'
import { createEffect, createSignal, on, onCleanup, Show } from 'solid-js'
import { cn } from '../lib/cn.ts'
import { Badge } from './badge.tsx'

const stateLabels = {
  waiting: 'Очікує',
  ready: 'Готове до роботи',
  running: 'У роботі',
  paused: 'На паузі',
  done: 'Завершено',
} as const

export const formatWorkDuration = (seconds: number): string => {
  const minutes = Math.floor(Math.max(0, seconds) / 60)
  return `${Math.floor(minutes / 60)} год ${minutes % 60} хв`
}

export const ProductionWorkTime = (props: { seconds: number; runningSince: string | null }) => {
  const [now, setNow] = createSignal(Date.now())
  const [receivedAt, setReceivedAt] = createSignal(Date.now())
  createEffect(
    on(
      () => [props.seconds, props.runningSince],
      () => {
        const received = Date.now()
        setReceivedAt(received)
        setNow(received)
        if (!props.runningSince) return
        const timer = window.setInterval(() => setNow(Date.now()), 1000)
        onCleanup(() => window.clearInterval(timer))
      },
    ),
  )
  const seconds = () =>
    props.seconds + (props.runningSince ? Math.max(0, (now() - receivedAt()) / 1000) : 0)
  return <span class="font-mono tabular-nums">{formatWorkDuration(seconds())}</span>
}

export const ProductionWorkStatus = (props: { state: ProductionWorkTask['state'] }) => (
  <Badge
    variant={
      props.state === 'done'
        ? 'done'
        : props.state === 'running'
          ? 'active'
          : props.state === 'paused'
            ? 'warning'
            : 'neutral'
    }
  >
    {stateLabels[props.state]}
  </Badge>
)

export interface ProductionWorkCardProps {
  task: ProductionWorkTask
  touch?: boolean
  onOpen: () => void
}

export const ProductionWorkCard = (props: ProductionWorkCardProps) => (
  <article class="min-w-0 border-b border-border">
    <button
      type="button"
      onClick={() => props.onOpen()}
      aria-label={`Відкрити завдання: ${props.task.operationName}, ${props.task.itemName}`}
      class={cn(
        'grid w-full min-w-0 cursor-pointer grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-2 rounded-control py-2.5 text-left transition-colors duration-(--duration-press) ease-out hover:bg-surface-muted focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_auto] lg:grid-cols-[minmax(0,2fr)_minmax(0,1.2fr)_minmax(0,1fr)_auto] lg:items-center',
        props.touch ? 'min-h-(--size-touch-lg)' : 'min-h-(--size-touch)',
      )}
    >
      <span class="col-span-2 grid min-w-0 content-start gap-1 sm:col-span-1 sm:row-span-2 lg:row-span-1">
        <span class="flex flex-wrap items-center gap-2">
          <span class="min-w-0 font-mono text-caption break-words text-text-muted">
            {props.task.orderNumber}
          </span>
          <ProductionWorkStatus state={props.task.state} />
          <Show when={props.task.priority === 'high'}>
            <span class="text-caption text-status-warning">Високий пріоритет</span>
          </Show>
        </span>
        <span class={cn('text-body font-semibold break-words text-text', props.touch && 'text-ui')}>
          {props.task.operationName} · {props.task.itemName}
        </span>
      </span>
      <span class="col-span-2 grid min-w-0 gap-1 break-words lg:col-span-1">
        <span class="text-body font-medium text-text">
          {props.task.equipmentName ?? 'Без обладнання'}
        </span>
        <span class="text-caption text-text-muted">
          {props.task.workCenterName ?? 'Без дільниці'} ·{' '}
          {props.task.assigneeName ?? 'Не призначено'}
        </span>
      </span>
      <span class="grid min-w-0 content-center gap-1 text-caption text-text-muted lg:text-right">
        <span class="break-words">
          Готово{' '}
          <span class="font-mono text-text tabular-nums">
            {props.task.goodQuantity} / {props.task.plannedQuantity ?? '—'}
          </span>{' '}
          {props.task.unitSymbol}
        </span>
        <span class="inline-flex items-center gap-1 lg:justify-end">
          <IconClock size={14} strokeWidth={1.5} aria-hidden="true" />
          <ProductionWorkTime
            seconds={props.task.workedSeconds}
            runningSince={props.task.runningSince}
          />
        </span>
      </span>
      <span
        aria-hidden="true"
        class={cn(
          'flex items-center justify-center justify-self-end text-text-muted',
          props.touch
            ? 'w-(--size-touch-lg)'
            : 'h-(--size-touch) w-(--size-touch) lg:h-(--size-control-sm) lg:w-(--size-control-sm)',
        )}
      >
        <IconChevronRight size={16} strokeWidth={1.5} />
      </span>
      <Show when={props.task.blockReason}>
        <span class="col-span-2 text-caption break-words text-status-warning sm:col-span-3 lg:col-span-4">
          {props.task.blockReason}
        </span>
      </Show>
    </button>
  </article>
)
